import { Dropdown, Choice, Alert } from '@cmsgov/ds-medicare-gov'
import { IconInfo } from 'components/Icon/IconInfo'
import { ContentLayout } from 'components/Layouts'
import { MyChoiceList } from 'components/ReactHookForm/MyChoiceList'
import { MyTextField } from 'components/ReactHookForm/MyTextField'
import { repRelationshipToBeneOptions } from 'data/beneficiary'
import { statesList } from 'data/states'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useForm, Controller, FormProvider, DeepMap, FieldError } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { getAuthReps, getLoadingState, IAuthRepsAction } from 'redux/reducers/auth-reps'
import { getErrorMessageController, prefixErrorText } from 'utils/react-hook-form'
import { Button } from 'components/Button/Button'
import { MyAcknowledge } from 'components/ReactHookForm/MyAcknowledge'
import { MyDevTool } from 'components/ReactHookForm/MyDevTool'
import { MyDateField } from 'components/ReactHookForm/MyDateField'
import { isStartDateValid, isEndDateValid, parseDate } from 'utils/dateAuthRep'
import { AuthRepPrintDrawer } from 'components/MyDrawer/drawers'
import { useHistory, useParams } from 'react-router-dom'
import { medicareApiClient } from 'clients/medicareApiClient'
import { addIsPastRepField } from '../isPastRep'
import { getModalType, setModal } from 'redux/reducers/modal'
import { buildAbsoluteMedicareMyUrl } from 'utils/urls/buildAbsoluteMedicareMyUrl'
import { MyTooltip } from 'components/MyTooltip'
import { AuthRepFormErrorAlert } from './AuthRepFormErrorAlert'
import { MySpinner } from 'components/MySpinner/MySpinner'
import { Component } from '@cms-max/medicare-api-sdk'

export interface AuthRepFormProps {
  handleOnSubmit: (data: any) => void;
  handleOnBack: (data: any) => void;
  handleOnError: (data: any) => void;
  // TODO - pass an isEdit prop to change the functionality of this component based on window URL
  isEdit?: boolean;
  isNewYorkResident?: boolean;
}

export const AuthRepForm: React.FC<AuthRepFormProps> = ({ handleOnSubmit, handleOnBack, handleOnError, isEdit, isNewYorkResident = false }) => {
  /*
   *
   * EditRep logic
   *
   */
  const { id } = useParams<{ id: string }>()
  const history = useHistory()
  const dispatch = useDispatch()

  /*
   *
   * Initialization Form Logic
   *
   */
  const { t } = useTranslation(['authRep', 'states'])
  const reduxFormData = useSelector(getAuthReps) // this is just what's in redux, and all fields are undefined initially
  const loadingState = useSelector(getLoadingState)
  const [pageLoading, setPageLoading] = useState(false)

  const defaultValuesObject = useMemo((): IAuthRepsAction => {
    return ({
      ...(isEdit ? { id: undefined } : {}), // PUT requires id, POST doesnt
      repFullName: reduxFormData.repFullName,
      organizationName: reduxFormData.organizationName,
      repAddress: {
        address1: reduxFormData.repAddress?.address1,
        zip: reduxFormData.repAddress?.zip,
        city: reduxFormData.repAddress?.city,
        state: reduxFormData.repAddress?.state
      },
      repRelationshipToBene: reduxFormData.repRelationshipToBene,
      disclosurePeriod: reduxFormData.disclosurePeriod,
      authStartDate: { // always default to today even if editing since the start date must be later than today
        day: new Date().getDate(),
        month: new Date().getMonth() + 1,
        year: new Date().getFullYear()
      },
      authEndDate: reduxFormData.authEndDate ?? {
        day: undefined,
        month: undefined,
        year: undefined
      },
      disclosureReason: reduxFormData.disclosureReason,
      disclosureLimitations: {
        limitedInformation: reduxFormData.disclosureLimitations.limitedInformation,
        // these must be true  or false for add and edit or the backend will break
        isInfoMedicareAllowedChecked: Boolean(reduxFormData.disclosureLimitations.isInfoMedicareAllowedChecked),
        isInfoMedicareClaimsChecked: Boolean(reduxFormData.disclosureLimitations.isInfoMedicareClaimsChecked),
        isInfoPlanEnrollmentChecked: Boolean(reduxFormData.disclosureLimitations.isInfoPlanEnrollmentChecked),
        isInfoPremiumPaymentsChecked: Boolean(reduxFormData.disclosureLimitations.isInfoPremiumPaymentsChecked),
        isOtherSpecificInfoChecked: Boolean(reduxFormData.disclosureLimitations.isOtherSpecificInfoChecked)
      },
      acknowledgeCheckbox: false, // always undefined to force acknowledge
      nyDrugInformation: reduxFormData.nyDrugInformation
    })
  }, [reduxFormData, isEdit])

  const methods = useForm<IAuthRepsAction>({
    mode: 'onTouched',
    reValidateMode: 'onChange',
    defaultValues: defaultValuesObject,
    shouldFocusError: false
  })

  const { handleSubmit, control, reset, formState, watch, setError, clearErrors, setValue, trigger } = methods
  const { errors } = formState
  const [currentRep, setCurrentRep] = useState<IAuthRepsAction>()

  useEffect(() => {
    let isMounted = true

    const getReps = async () => {
      setPageLoading(true)
      const reps = await medicareApiClient.asyncGetRepsByReqToken()
      const repsWithPastFlag = addIsPastRepField(reps)
      const currentRep = repsWithPastFlag.filter((rep : IAuthRepsAction) => rep.id === id)[0]
      setPageLoading(false)
      if (currentRep.isPastRep) {
        history.push('/account-settings/auth-rep')
      } else {
        if (currentRep) {
          if (isMounted === true) {
            // Call setCurrentRep to trigger an effect. If we try and `setValue` here, then it will likely be called before
            // the form is rendered, and the values won't be set at all
            setCurrentRep(currentRep)
          }
        }
      }
    }
    if (isEdit && id !== undefined) {
      getReps()
    }

    return () => { isMounted = false }
  }, [isEdit, id, history])

  // Initialize form to rep data when the current rep is set
  useEffect(() => {
    if (currentRep) {
      setValue('id', currentRep.id)
      setValue('repFullName', currentRep.repFullName)
      setValue('organizationName', currentRep.organizationName)
      if (currentRep.repAddress) {
        setValue('repAddress.address1', currentRep.repAddress.address1)
        setValue('repAddress.zip', currentRep.repAddress.zip)
        setValue('repAddress.city', currentRep.repAddress.city)
        setValue('repAddress.state', currentRep.repAddress.state)
      }
      setValue('repRelationshipToBene', currentRep.repRelationshipToBene)
      setValue('disclosurePeriod', currentRep.disclosurePeriod)
      setValue('authStartDate', currentRep.authStartDate)
      setValue('authEndDate', currentRep.authEndDate)
      setValue('disclosureReason', currentRep.disclosureReason)
      setValue('disclosureLimitations.limitedInformation', currentRep.disclosureLimitations.limitedInformation)
      setValue('disclosureLimitations.isInfoMedicareAllowedChecked', Boolean(currentRep.disclosureLimitations.isInfoMedicareAllowedChecked))
      setValue('disclosureLimitations.isInfoMedicareClaimsChecked', Boolean(currentRep.disclosureLimitations.isInfoMedicareClaimsChecked))
      setValue('disclosureLimitations.isInfoPlanEnrollmentChecked', Boolean(currentRep.disclosureLimitations.isInfoPlanEnrollmentChecked))
      setValue('disclosureLimitations.isInfoPremiumPaymentsChecked', Boolean(currentRep.disclosureLimitations.isInfoPremiumPaymentsChecked))
      setValue('disclosureLimitations.isOtherSpecificInfoChecked', Boolean(currentRep.disclosureLimitations.isOtherSpecificInfoChecked))
      setValue('disclosureLimitations.otherSpecificInformation', currentRep.disclosureLimitations.otherSpecificInformation)
      setValue('acknowledgeCheckbox', false) // always false to force acknowledge
      setValue('nyDrugInformation', currentRep.nyDrugInformation)
    } else {
      reset(defaultValuesObject, {
        keepErrors: true
      })
    }
  }, [currentRep, setValue, defaultValuesObject, reset])

  useEffect(() => {
    // do nothing if form is already submitted, we want the form state to stay as it was when the user hit submit until it succeeds
    if (!formState.isSubmitted) {
      reset(defaultValuesObject, {
        keepErrors: true
      })
    }
  }, [loadingState, defaultValuesObject, reset, formState.isSubmitted])

  /*
   *
   * Drawer Logic
   *
   */
  const modalTypeOpen = useSelector(getModalType)
  const openPrintableFormDrawer = () => {
    if (modalTypeOpen === 'auth-print') {
      dispatch(setModal({ modalType: undefined }))
    } else {
      dispatch(setModal({ modalType: 'auth-print' }))
    }
  }

  /*
   *
   * Form Submission Logic and Alerts, and LimitedInfo/OtherSpecificInfo
   *
   */
  const openPrintDrawerRef = useRef<HTMLAnchorElement | HTMLButtonElement>(null)
  const alertRef = useRef<HTMLDivElement | null>(null)
  const alertLimitedRef = useRef<HTMLDivElement | null>(null)

  const setFocusOnAlert = () => {
    alertRef.current?.focus()
  }

  useEffect(() => {
    if (loadingState === 'error') {
      setFocusOnAlert() // If API request fails, focus on errors
    }
  }, [loadingState])

  const checkFormSubmit = (data:IAuthRepsAction) => {
    if (isLimitedInformation && isAllCheckboxValuesFalse) {
      setError('disclosureLimitations.isOtherSpecificInfoChecked', {
        type: 'manual',
        message: t('incompleteInformation.discloseLimitedInformation')
      })
      window.scrollTo({ top: 0, left: 0, behavior: 'smooth' })
    } else if ((!validAuthStartDate || !validAuthEndDate) && isSpecificDisclosurePeriod) {
      if (!validAuthStartDate) {
        setError('authStartDate', {
          type: 'manual',
          message: t('invalidInformation.disclosurePeriod.authStartDate')
        })
      }
      if (!validAuthEndDate) {
        setError('authEndDate', {
          type: 'manual',
          message: t('invalidInformation.disclosurePeriod.authEndDate')
        })
      }
    } else {
      clearErrors('disclosureLimitations.isOtherSpecificInfoChecked')
      clearErrors('authStartDate')
      clearErrors('authEndDate')
      handleOnSubmit(data)
    }
  }

  const checkErrorSubmit = (data:DeepMap<IAuthRepsAction, FieldError>) => {
    setFocusOnAlert() // only works in chrome if user tabs and enters the submit buttom via keyboard
    handleOnError(data)
  }

  const clearManualErrorsIfValid = () => {
    // Verify manually set errors are still actually needed
    if (errors.authStartDate && validAuthStartDate) clearErrors('authStartDate')
    if (errors.authEndDate && validAuthEndDate) clearErrors('authEndDate')
  }

  /*
   *
   * limited_information / otherSpecificInformation specific code / 5 checkboxes
   *
   */
  const watchCheckboxInputs = watch(['disclosureLimitations.isInfoMedicareAllowedChecked',
    'disclosureLimitations.isInfoMedicareClaimsChecked',
    'disclosureLimitations.isInfoPlanEnrollmentChecked',
    'disclosureLimitations.isInfoPremiumPaymentsChecked',
    'disclosureLimitations.isOtherSpecificInfoChecked'
  ])
  const isLimitedInformation = (watch('disclosureLimitations.limitedInformation') === 'limited_information')// watch if the name is checked
  const isOtherSpecificInfoCheckedTrue = watch('disclosureLimitations.isOtherSpecificInfoChecked')
  const otherSpecificInformationLength = watch('disclosureLimitations.otherSpecificInformation')?.length ?? 0

  // Limited Information & Other Specific Information Logic
  const [isAllCheckboxValuesFalse, setAllCheckboxesValuesFalse] = useState<boolean>(false)

  // This one controls the errors for the below useEffect state call
  useEffect(() => {
    if (isLimitedInformation && isAllCheckboxValuesFalse) {
      // we made a conscious decision not to have this error show before submit because there are instructions for selecting a checkbox
      // accessibility team Yvonne determined that this was the best option since she doesn't want the screen reader to yell immediately when the option is selected.
      if (formState.isSubmitted) {
        // TODO: This does not currently show on the page. We should pass in the translated content here and show this error instead of what we currently show
        setError('disclosureLimitations.isOtherSpecificInfoChecked', {
          type: 'manual',
          message: 'Please select at least one checkbox'
        })
      }
    } else {
      clearErrors('disclosureLimitations.isOtherSpecificInfoChecked')
    }
  }, [isAllCheckboxValuesFalse, isLimitedInformation, formState.isSubmitted, clearErrors, setError])

  // This controls a state variable being set
  useEffect(() => {
    if (Object.values(watchCheckboxInputs).every(v => !v)) {
      setAllCheckboxesValuesFalse(true)
    } else {
      setAllCheckboxesValuesFalse(false)
    }
  }, [watchCheckboxInputs, formState.isSubmitted, trigger, clearErrors])

  useEffect(() => {
    if (!isOtherSpecificInfoCheckedTrue) {
      clearErrors('disclosureLimitations.otherSpecificInformation')
    } else {
      if (formState.isSubmitted) {
        trigger('disclosureLimitations.otherSpecificInformation')
      }
    }
  }, [isOtherSpecificInfoCheckedTrue, trigger, formState.isSubmitted, clearErrors, isEdit])

  /*
   *
   * Date Based Logic
   *
   */

  const isSpecificDisclosurePeriod = (watch('disclosurePeriod') === 'disclose_specific_time')// watch if the name is checked
  const watchStartDate = watch('authStartDate')
  const watchEndDate = watch('authEndDate')

  const validAuthStartDate = useMemo(() => isStartDateValid(parseDate(watchStartDate?.month, watchStartDate?.day, watchStartDate?.year)), [
    watchStartDate?.month, watchStartDate?.day, watchStartDate?.year
  ])
  const validAuthEndDate = useMemo(() => {
    return isEndDateValid(parseDate(watchStartDate?.month, watchStartDate?.day, watchStartDate?.year), (parseDate(watchEndDate?.month, watchEndDate?.day, watchEndDate?.year)))
  }, [watchStartDate?.month, watchStartDate?.day, watchStartDate?.year, watchEndDate?.month, watchEndDate?.day, watchEndDate?.year])

  useEffect(() => {
    if (isSpecificDisclosurePeriod) {
      setValue('authStartDate', {
        day: new Date().getDate(),
        month: new Date().getMonth() + 1,
        year: new Date().getFullYear()
      })
    }
  }, [isSpecificDisclosurePeriod, setValue])

  /*
   *
   * Render Code
   *
   */

  return (
    <ContentLayout heading={t('pageTitle')}>
      {modalTypeOpen === 'auth-print' && (
        <AuthRepPrintDrawer onClose={openPrintableFormDrawer} />
      )}
      {pageLoading
        ? <div className='ds-l-container my-u-container--8'>
            <div className='ds-l-row ds-u-justify-content--left'>
              <MySpinner size='large'/>
            </div>
          </div>
        : <FormProvider {...methods}>
            <form data-testid='form-add-rep' onSubmit={(e) => {
              e.preventDefault()
              clearManualErrorsIfValid()
              handleSubmit(checkFormSubmit, checkErrorSubmit)()
            }}>
              {process.env.NODE_ENV === 'development' && <MyDevTool />}
              <div className='ds-l-container'>
                <div className='ds-l-form-row ds-u-justify-content--center'>
                  <div className='ds-l-col--12 ds-l-md-col--10 ds-l-lg-col--8'>
                    <p className='ds-u-margin-bottom--0 my-u-font-size--16'>{t('paragraph.text')}</p>
                    <Button
                      className='ds-u-margin-bottom--0 ds-c-button ds-c-button--ghost ds-u-padding--0 modal-blue ds-u-text-align--left my-u-font-size--16'
                      ref={openPrintDrawerRef}
                      siteActivityProps={{
                        component: Component.AuthorizedRepresentative,
                        clickData: {
                          linkText: t('paragraph.linkText', { lng: 'en' })
                        }
                      }}
                      onClick={openPrintableFormDrawer}
                      data-testid='auth-rep-print-drawer'
                    >
                      {t('paragraph.linkText')}
                      <div className='ds-u-display--inline-block'>
                        <IconInfo className='my-c-my-svg-icon--primary-alt'/>
                      </div>
                    </Button>
                    <h2 className='ds-text-heading--2xl my-u-mt--20 my-u-mb--5'>{t('pageHeading')}</h2>
                    <span className='my-c-required-text my-u-mb--6'>{t('pageCaption')}</span>
                    <p className='ds-u-margin-bottom--2 ds-u-font-size--md'>{t('formParagraph')}</p>
                    <Alert
                      className='my-u-mb--12'
                      hideIcon
                    >
                      {t('most-fields-do-not-accept-special-characters')}
                    </Alert>
                    <div ref={alertRef} tabIndex={-1}>
                      {formState.isSubmitted && (loadingState === 'error' || Object.keys(errors).length > 0) &&
                        <div className='my-u-mb--20 ds-c-alert ds-c-alert--error ds-c-alert--hide-icon' data-testid='add-rep-error'>
                          <div className='ds-c-alert__body'>
                            <div className='ds-c-alert__text'>
                            {loadingState === 'error' &&
                            <div aria-live="polite" aria-atomic="true" >
                              <h2 className='ds-c-alert__heading'>Error</h2>
                              {t('incompleteInformation.failedRequest')}
                            </div>}
                            {Object.keys(errors).length > 0 &&
                              <>
                                <h2 className='ds-c-alert__heading'>{prefixErrorText(t('errorAlertTitle'))}</h2>
                                <AuthRepFormErrorAlert
                                  errors={errors}
                                  isAllCheckboxValuesFalse={isAllCheckboxValuesFalse}
                                  isLimitedInformation={isLimitedInformation}
                                  isNewYorkResident={isNewYorkResident}
                                  isSpecificDisclosurePeriod={isSpecificDisclosurePeriod}
                                  isSubmitted={formState.isSubmitted}
                                  setFocusOnAlert={setFocusOnAlert}
                                  validAuthEndDate={validAuthEndDate}
                                  validAuthStartDate={validAuthStartDate}
                                />
                              </>}
                            </div>
                          </div>
                        </div>
                      }
                    </div>
                    {/* eslint-disable-next-line operator-linebreak */}
                    <div className='ds-l-form-row'>
                      <div className='ds-l-col--12 ds-l-md-col--10 ds-l-lg-col--8'>
                        <MyTextField
                          name='repFullName'
                          rules={{
                            required: { value: true, message: t('incompleteInformation.repFullName') },
                            pattern: { value: /^[a-zA-Z0-9 .'-]+$/, message: t('invalidRegex.genericSpecialCharactersError') }
                          }}
                          label={t('fieldLabels.repFullName')}
                          maxLength={75}
                          addErrorPrefix
                        />
                      </div>
                    </div>
                    <div className='ds-l-form-row'>
                      <div className='ds-l-col--12 ds-l-md-col--10 ds-l-lg-col--8'>
                        <MyTextField
                          name='organizationName'
                          label={t('fieldLabels.organizationName')}
                          rules={{
                            pattern: { value: /^[a-zA-Z0-9 &.'-]+$/, message: t('invalidRegex.genericSpecialCharactersError') }
                          }}
                          maxLength={75}
                          addErrorPrefix
                        />
                      </div>
                    </div>
                    <div className='ds-l-form-row'>
                      <div className='ds-l-col--12 ds-l-md-col--10 ds-l-lg-col--8'>
                        <MyTextField
                          name='repAddress.address1'
                          rules={{
                            required: { value: true, message: t('incompleteInformation.address1') },
                            pattern: { value: /^[A-Za-z0-9 #/,.'-]+$/, message: t('invalidRegex.genericSpecialCharactersError') }
                          }}
                          label={t('fieldLabels.address1')}
                          maxLength={100}
                          addErrorPrefix
                        />
                      </div>
                    </div>
                    <div className='ds-l-form-row'>
                      <div className='ds-l-col--12 ds-l-md-col--10 ds-l-lg-col--8'>
                        <MyTextField
                          name='repAddress.city'
                          rules={{
                            required: { value: true, message: t('incompleteInformation.city') },
                            pattern: { value: /^[A-Za-z0-9 #/.'-]+$/, message: t('invalidRegex.genericSpecialCharactersError') }
                          }}
                          label={t('fieldLabels.city')}
                          maxLength={100}
                          addErrorPrefix
                        />
                      </div>
                    </div>
                    <div className='ds-l-form-row'>
                      <div className='ds-l-sm--12 ds-l-lg-col--4'>
                        <MyTextField
                          name='repAddress.zip'
                          rules={
                            {
                              required: { value: true, message: t('incompleteInformation.zip') },
                              maxLength: { value: 100, message: t('invalidInformation.zip') },
                              pattern: { value: /^[a-zA-Z0-9 -]+$/, message: t('invalidInformation.zip') }
                            }
                          }
                          maxLength={100}
                          label={t('fieldLabels.zip')}
                          addErrorPrefix
                        />
                      </div>
                      <div className='ds-l-sm--12 ds-l-lg-col--4'>
                        <Controller
                          rules={{ required: { value: true, message: t('incompleteInformation.state') } }}
                          render={({ field, fieldState }) => (
                            <Dropdown
                              aria-describedby={fieldState.error ? `${field.name}-error` : undefined}
                              data-testid='repAddress.state'
                              errorMessage={getErrorMessageController(fieldState, true)}
                              errorPlacement='bottom'
                              options={[
                                { label: t('incompleteInformation.state'), value: '' },
                                ...statesList.map((option) => {
                                  const newOption = { label: t(option.value, { ns: 'states' }), value: option.value }
                                  return newOption
                                })
                              ]}
                              label={t('fieldLabels.state')}
                              name={field.name}
                              id={field.name}
                              inputRef={field.ref as any}
                              // eslint-disable-next-line react/jsx-handler-names
                              onChange={field.onChange}
                              // eslint-disable-next-line react/jsx-handler-names
                              onBlur={field.onBlur}
                              value={field.value}
                            />
                          )}
                          name='repAddress.state'
                          control={control}
                        />
                      </div>
                    </div>
                    <div className='ds-l-form-row'>
                      <div className='ds-l-col--12 ds-l-md-col--10 ds-l-lg-col--8 my-c-text-field-margin ds-u-margin-bottom--7'>
                        <Controller
                          // rules={{ required: { value: true, message: t('incompleteInformation.repRelationshipToBene') } }}
                          render={({ field, fieldState }) => (
                            <Dropdown
                              aria-describedby={fieldState.error ? `error-${field.name}` : undefined}
                              errorMessage={getErrorMessageController(fieldState, true)}
                              errorPlacement='bottom'
                              options={[
                                { label: t('repRelationshipToBeneDropDown.default'), value: '' },
                                ...repRelationshipToBeneOptions.map((option) => {
                                  const newOption = { label: t(option.label), value: option.value }
                                  return newOption
                                })
                              ]}
                              label={t('fieldLabels.repRelationshipToBene')}
                              name={field.name}
                              id={field.name}
                              inputRef={field.ref as any}
                              // eslint-disable-next-line react/jsx-handler-names
                              onChange={field.onChange}
                              // eslint-disable-next-line react/jsx-handler-names
                              onBlur={field.onBlur}
                              value={field.value}
                            />
                          )}
                          name='repRelationshipToBene'
                          control={control}
                        />
                      </div>
                    </div>
                    <div className='ds-l-form-row'>
                      <div className='ds-l-col--12 ds-l-md-col--10 ds-l-lg-col--8'>
                        <h2 className='ds-text-heading--2xl my-u-mt--6'>{t('fieldLabels.disclosurePeriod.heading')}</h2>
                      </div>
                    </div>
                    <div className='ds-l-form-row'>
                      <div className='ds-l-col--12 ds-l-md-col--10 ds-l-lg-col--8 my-u-mb--4'>
                        <MyChoiceList
                          variableInputName='disclosureLimitations.limitedInformation'
                          label={t('fieldLabels.discloseInfo.main')}
                          choices={[
                            {
                              label: t('fieldLabels.discloseInfo.radio1'),
                              value: 'any_information',
                              // If NY
                              disabled: isNewYorkResident,
                              customToolTip: (
                                isNewYorkResident
                                  ? (
                                  <MyTooltip
                                    className='ds-c-tooltip__trigger-link'
                                    component='a'
                                    maxWidth='600px'
                                    placement='bottom'
                                    offset={[0, 10]}
                                    data-testid='any_inforation_tooltip'
                                    title={
                                      <div className='my-u-p--5'>
                                        <h2 className='my-u-mt--0'>{t('fieldLabels.discloseInfo.nyToolTipLink')}</h2>
                                        <p>{t('fieldLabels.discloseInfo.nyToolTipAnyInfoText.para1')}</p>
                                        <p>{t('fieldLabels.discloseInfo.nyToolTipAnyInfoText.para2')}</p>
                                        <p>{t('fieldLabels.discloseInfo.nyToolTipAnyInfoText.para3')}</p>
                                        <ul>
                                          <li>{t('fieldLabels.discloseInfo.nyToolTipAnyInfoText.li1')}</li>
                                          <li>{t('fieldLabels.discloseInfo.nyToolTipAnyInfoText.li2')}</li>
                                        </ul>
                                      </div>
                                    }
                                    siteActivityProps={{
                                      component: Component.AuthorizedRepresentative,
                                      linkText: t('fieldLabels.discloseInfo.nyToolTipLink', { lng: 'en' })
                                    }}
                                  >
                                    {t('fieldLabels.discloseInfo.nyToolTipLink')}
                                  </MyTooltip>)
                                  : null
                              )
                            },
                            {
                              label: t('fieldLabels.discloseInfo.radio2'),
                              value: 'limited_information',
                              customToolTip: (
                                <MyTooltip
                                  className='ds-c-tooltip__trigger-link'
                                  component='a'
                                  maxWidth='600px'
                                  placement='bottom'
                                  offset={[0, 10]}
                                  data-testid='limited_information_tooltip'
                                  title={
                                    <div className='my-u-p--5'>
                                      <p>{t('fieldLabels.discloseInfo.toolTipText.text')}</p>
                                      <ul>
                                        <li>{t('fieldLabels.discloseInfo.toolTipText.info1')}</li>
                                        <li>{t('fieldLabels.discloseInfo.toolTipText.info2')}</li>
                                        <li>{t('fieldLabels.discloseInfo.toolTipText.info3')}</li>
                                        <li>{t('fieldLabels.discloseInfo.toolTipText.info4')}</li>
                                        <li>{t('fieldLabels.discloseInfo.toolTipText.info5')}</li>
                                      </ul>
                                    </div>
                                  }
                                  siteActivityProps={{
                                    component: Component.AuthorizedRepresentative,
                                    linkText: t('fieldLabels.discloseInfo.toolTipLink', { lng: 'en' })
                                  }}
                                >
                                  {t('fieldLabels.discloseInfo.toolTipLink')}
                                </MyTooltip>)
                            }
                          ]}
                          errorMessageString={prefixErrorText(t('incompleteInformation.limitedInformation'))}
                        />
                        {errors.disclosureLimitations?.limitedInformation?.message && (
                          <span
                            id='error-disclosureLimitations.limitedInformation'
                            data-testid='limited-info-form-error'
                            className='my-u-mt--3 error-message ds-c-field__error-message'
                          >
                            {prefixErrorText(t('incompleteInformation.limitedInformation'))}
                          </span>
                        )}
                      </div>
                      {isLimitedInformation &&
                      (
                        <div className='ds-l-col--12'>
                          <Alert
                            hideIcon
                            tabIndex={-1}
                            id='limitedInfoAlert'
                            ref={alertLimitedRef as any}
                            className='my-u-outline-focus--black'
                          >
                            <p className='my-u-m--0'>{t('fieldLabels.discloseInfo.alert')}</p>
                          </Alert>
                          <div className='ds-u-margin-y--1'>
                            <Controller
                              render={({ field }) => (
                                <Choice
                                  aria-describedby={errors.disclosureLimitations?.isOtherSpecificInfoChecked ? 'error-disclosureLimitations.isOtherSpecificInfoChecked' : undefined}
                                  data-testid={field.name}
                                  name={field.name}
                                  inputRef={field.ref as any}
                                  onChange={field.onChange as any}
                                  type='checkbox'
                                  label={t('fieldLabels.discloseInfo.checkbox1')}
                                  value={field.value as any}
                                  checked={field.value as any}
                                />

                              )}
                              name='disclosureLimitations.isInfoMedicareAllowedChecked'
                              control={control}
                            />
                          </div>
                          <div className='ds-u-margin-y--2'>
                            <Controller
                              render={({ field }) => (
                                <Choice
                                  aria-describedby={errors.disclosureLimitations?.isOtherSpecificInfoChecked ? 'error-disclosureLimitations.isOtherSpecificInfoChecked' : undefined}
                                  data-testid={field.name}
                                  name={field.name}
                                  inputRef={field.ref as any}
                                  onChange={field.onChange as any}
                                  type='checkbox'
                                  label={t('fieldLabels.discloseInfo.checkbox2')}
                                  value={field.value as any}
                                  checked={field.value as any}
                                />

                              )}
                              name='disclosureLimitations.isInfoMedicareClaimsChecked'
                              control={control}
                            />
                          </div>
                          <div className='ds-u-margin-y--2'>
                            <Controller
                              render={({ field }) => (
                                <Choice
                                  aria-describedby={errors.disclosureLimitations?.isOtherSpecificInfoChecked ? 'error-disclosureLimitations.isOtherSpecificInfoChecked' : undefined}
                                  data-testid={field.name}
                                  name={field.name}
                                  inputRef={field.ref as any}
                                  onChange={field.onChange as any}
                                  type='checkbox'
                                  label={t('fieldLabels.discloseInfo.checkbox3')}
                                  value={field.value as any}
                                  checked={field.value as any}
                                />

                              )}
                              name='disclosureLimitations.isInfoPlanEnrollmentChecked'
                              control={control}
                            />
                          </div>
                          <div className='ds-u-margin-y--2'>
                            <Controller
                              render={({ field }) => (
                                <Choice
                                  aria-describedby={errors.disclosureLimitations?.isOtherSpecificInfoChecked ? 'error-disclosureLimitations.isOtherSpecificInfoChecked' : undefined}
                                  data-testid={field.name}
                                  name={field.name}
                                  inputRef={field.ref as any}
                                  onChange={field.onChange as any}
                                  type='checkbox'
                                  label={t('fieldLabels.discloseInfo.checkbox4')}
                                  value={field.value as any}
                                  checked={field.value as any}
                                />

                              )}
                              name='disclosureLimitations.isInfoPremiumPaymentsChecked'
                              control={control}
                            />
                          </div>
                          <div className='ds-u-margin-y--2'>
                            <Controller
                              name='disclosureLimitations.isOtherSpecificInfoChecked'
                              render={({ field }) => (
                                <Choice
                                  aria-describedby={errors.disclosureLimitations?.isOtherSpecificInfoChecked ? `error-${field.name}` : undefined}
                                  data-testid={field.name}
                                  name={field.name}
                                  // inputRef={field.ref as any}
                                  onChange={field.onChange as any}
                                  type='checkbox'
                                  label={t('fieldLabels.discloseInfo.checkbox5')}
                                  value={field.value as any}
                                  checked={field.value as any}
                                  checkedChildren={
                                    <>
                                      <MyTextField
                                        name='disclosureLimitations.otherSpecificInformation'
                                        fieldClassName='my-u-min-width--full'
                                        rules={{
                                          required: { value: true, message: t('fieldLabels.discloseInfo.textOther') },
                                          maxLength: { value: 75, message: 'Max Character Limit of 75' },
                                          pattern: { value: /^[a-zA-Z0-9 .,'-]+$/, message: t('invalidRegex.genericSpecialCharactersError') }
                                        }}
                                        ariaLabel={t('fieldLabels.discloseInfo.textOther')}
                                        data-testid='disclosureLimitations.otherSpecificInformation'
                                        placeholder={t('fieldLabels.discloseInfo.textOther')}
                                        maxLength={75}
                                        shouldUnregister
                                        addErrorPrefix
                                      />
                                      <div data-testid='other-specific-info-char-count' className='my-u-color--medium-gray'>{otherSpecificInformationLength}/75 {t('fieldLabels.discloseInfo.textOtherCharLimit')}</div>
                                    </>
                                  }
                                />
                              )}
                              control={control}
                            />
                          </div>
                          {errors.disclosureLimitations?.isOtherSpecificInfoChecked?.message && (
                            <span
                              id='error-disclosureLimitations.isOtherSpecificInfoChecked'
                              data-testid='limited-info-form-error'
                              className='error-message ds-c-field__error-message'
                            >
                              {prefixErrorText(t('incompleteInformation.discloseLimitedCheckbox'))}
                            </span>
                          )}

                        </div>
                      )}
                    </div>
                    {/* New York Residents */}
                    {isNewYorkResident && (
                      <div className='ds-l-form-row'>
                        <div className='ds-l-col--12 ds-l-md-col--10 ds-l-lg-col--8'>
                          <MyChoiceList
                            variableInputName='nyDrugInformation'
                            label={t('fieldLabels.discloseInfo.nyDrugInformation.title')}
                            choices={[
                              {
                                label: t('fieldLabels.discloseInfo.nyDrugInformation.radio1'),
                                value: 'include_information'
                              },
                              {
                                label: t('fieldLabels.discloseInfo.nyDrugInformation.radio2'),
                                value: 'exclude_information'
                              }
                            ]}
                            errorMessageString={t('incompleteInformation.nyDrugInformation')}
                            addErrorPrefix
                          />
                        </div>
                      </div>
                    )}
                    <div className='ds-l-form-row'>

                      <div className='ds-l-col--12 ds-l-md-col--10 ds-l-lg-col--8'>
                        <MyChoiceList
                          variableInputName='disclosurePeriod'
                          label={t('fieldLabels.disclosurePeriod.main')}
                          choices={[
                            {
                              label: t('fieldLabels.disclosurePeriod.discloseIndefinitely'),
                              value: 'disclose_indefinitely'
                            },
                            {
                              label: t('fieldLabels.disclosurePeriod.discloseSpecificTimePeriod'),
                              value: 'disclose_specific_time'
                            }
                          ]}
                          errorMessageString={t('incompleteInformation.disclosurePeriod')}
                          addErrorPrefix
                        />
                      </div>
                      {isSpecificDisclosurePeriod &&
                        <div className='ds-l-col--12'>
                          <MyDateField
                            isCurrentlyValid={validAuthStartDate || !formState.isSubmitted}
                            dashLineEndingEnabled
                            aria-describedby={!validAuthStartDate ? `error-authStartDate` : undefined}
                            variableInputName='authStartDate'
                            className='ch-u-scroll-margin--120'
                            shouldUnregister
                            fieldErrorStrings={{
                              month: {
                                required: prefixErrorText(t('incompleteInformation.authStartMonth')),
                                invalid: prefixErrorText(t('invalidInformation.authStartMonth'))
                              },
                              day: {
                                required: prefixErrorText(t('incompleteInformation.authStartDay')),
                                invalid: prefixErrorText(t('invalidInformation.authStartDay'))
                              },
                              year: {
                                required: prefixErrorText(t('incompleteInformation.authStartYear')),
                                invalid: prefixErrorText(t('invalidInformation.authStartYear'))
                              }
                            }}
                            legendHeader={
                              <legend>
                                <label htmlFor='authStartDate'><h5 className='ds-text-heading--md'>{t('fieldLabels.disclosurePeriod.beginningLabel')}</h5></label>

                                <span className='ds-c-field__hint ds-u-font-size--base my-u-mt--8'>{t('Use the format MM/DD/YYYY')}</span>
                              </legend>
                            }
                          />
                          {!validAuthStartDate && formState.isSubmitted && !(errors.authStartDate?.day?.message || errors.authStartDate?.month?.message || errors.authStartDate?.year?.message) &&
                            <span id='error-authStartDate' className='error-message ds-c-field__error-message'>
                              {prefixErrorText(t('invalidInformation.disclosurePeriod.authStartDate'))}
                            </span>}
                          <MyDateField
                            isCurrentlyValid={validAuthEndDate || !formState.isSubmitted}
                            aria-describedby={!validAuthEndDate ? `error-authEndDate` : undefined}
                            variableInputName='authEndDate'
                            className='ch-u-scroll-margin--120'
                            shouldUnregister
                            fieldErrorStrings={{
                              month: {
                                required: prefixErrorText(t('incompleteInformation.authEndMonth')),
                                invalid: prefixErrorText(t('invalidInformation.authEndMonth'))
                              },
                              day: {
                                required: prefixErrorText(t('incompleteInformation.authEndDay')),
                                invalid: prefixErrorText(t('invalidInformation.authEndDay'))
                              },
                              year: {
                                required: prefixErrorText(t('incompleteInformation.authEndYear')),
                                invalid: prefixErrorText(t('invalidInformation.authEndYear'))
                              }
                            }}
                            legendHeader={
                              <legend>
                                <label htmlFor='authEndDate'><h5 className='ds-text-heading--md'>{t('fieldLabels.disclosurePeriod.endingLabel')}</h5></label>
                                <span className='ds-c-field__hint ds-u-font-size--base my-u-mt--8'>{t('Use the format MM/DD/YYYY')}</span>
                                <span />
                              </legend>
                            }
                          />
                          {(!validAuthEndDate && formState.isSubmitted) && !(errors.authEndDate?.day?.message || errors.authEndDate?.month?.message || errors.authEndDate?.year?.message) &&
                            <span id='error-authEndDate' className='error-message ds-c-field__error-message'>
                              {prefixErrorText(t('invalidInformation.disclosurePeriod.authEndDate'))}
                            </span>}
                        </div>}
                    </div>
                    <div className='ds-l-form-row ds-u-margin-bottom--7'>
                      <div className='ds-l-col--12 ds-l-md-col--10 ds-l-lg-col--8'>
                        <MyTextField
                          name='disclosureReason'
                          rules={{
                            required: { value: true, message: t('incompleteInformation.disclosureReason') },
                            pattern: { value: /^[A-Za-z0-9ÁÄÉÈÍÏÓÖÚÜÇÑáäéèíïóöúüçñ /.'-]+$/, message: t('invalidRegex.genericSpecialCharactersError') }
                          }}
                          label={t('fieldLabels.disclosureReason')}
                          maxLength={300}
                          addErrorPrefix
                        />
                      </div>
                    </div>
                    <div className='ds-l-form-row '>
                      <div className='ds-l-col--12 ds-l-md-col--10 ds-l-lg-col--8'>
                        <h2 className='ds-text-heading--2xl my-u-mt--0 my-u-mb--5'>{t('fieldLabels.acknowledgeCheckbox.continueCheck')}</h2>
                      </div>
                    </div>
                    <div className='ds-l-form-row '>
                      <div className='ds-l-col--12'>
                        {/* -------------------------------------------------- */}
                        <MyAcknowledge
                          variableInputName='acknowledgeCheckbox'
                          heading={t('fieldLabels.acknowledgeCheckbox.heading')}
                          label={t('fieldLabels.acknowledgeCheckbox.main')}
                          ariaLabel={t('fieldLabels.acknowledgeCheckbox.ariaLabel')}
                          acknowledgements={[
                            {
                              value: t('fieldLabels.acknowledgeCheckbox.ack1')
                            },
                            {
                              value: t('fieldLabels.acknowledgeCheckbox.ack2')
                            },
                            {
                              value: t('fieldLabels.acknowledgeCheckbox.ack3')
                            },
                            {
                              value: t('fieldLabels.acknowledgeCheckbox.ack4')
                            }
                          ]}
                          errorMessageString={t('incompleteInformation.acknowledgeCheckbox')}
                          addErrorPrefix
                        />
                      </div>
                    </div>
                    <div className='my-c-text-field-margin'>
                      <Button
                        className='ds-c-button ds-c-button--solid my-u-mr--10 ds-u-margin-y--2'
                        type='submit'
                        data-testid='add-rep-next-btn'
                        siteActivityProps={{
                          component: Component.AuthorizedRepresentative,
                          clickData: {
                            linkText: t('button.submit', { lng: 'en' }),
                            linkUrl: buildAbsoluteMedicareMyUrl('/account-settings/auth-rep')
                          }
                        }}
                      >
                        {loadingState === 'loading' && (
                          <MySpinner size='small' inverse isInButton/>
                        )}
                        {t('button.submit')}
                      </Button>
                      <Button
                        data-testid='add-rep-cancel-btn'
                        className='ds-c-button'
                        onClick={handleOnBack}
                        siteActivityProps={{
                          component: Component.AuthorizedRepresentative,
                          clickData: {
                            linkText: t('button.cancel', { lng: 'en' }),
                            linkUrl: buildAbsoluteMedicareMyUrl('/account-settings/auth-rep')
                          }
                        }}
                      >
                        {t('button.cancel')}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>

            </form>
          </FormProvider>}
    </ContentLayout>
  )
}
